import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { filterFactory } from "@similarweb/pro-logic-components";
import type { SelectOption } from "@similarweb/pro-logic-components/dist/filter-factory/src/compound";
import { Flex, TruncatedText } from "@similarweb/ui-components/foundations";

import { createMultiSelectLabel } from "app/pages/digital-marketing/ad-intelligence/shared/filters/label-helpers";

import type { IVerificationFilterState } from "./verification-filters.types";

const CAMPAIGNS_POPOVER_PROPS = { style: { width: 420 } };

const renderCampaignOption = (item: SelectOption) => (
    <TruncatedText
        fontVariant="body2"
        title={item.label}
    >
        {item.label}
    </TruncatedText>
);

const createSelectedItemsTooltip = (options: SelectOption[]) => (value: string[] | undefined) => {
    if (!value?.length) {
        return null;
    }
    const labels = value.map((id) => options.find((o) => o.value === id)?.label ?? id);
    return (
        <Flex flexDirection="column">
            {labels.map((label) => (
                <span key={label}>{label}</span>
            ))}
        </Flex>
    );
};

const Filters = filterFactory.createTypedFilters<IVerificationFilterState>();

interface IVerificationFiltersProps {
    state: IVerificationFilterState;
    onChange: (next: IVerificationFilterState) => void;
    publisherOptions: SelectOption[];
    campaignOptions: SelectOption[];
    isPublishersLoading: boolean;
    isCampaignsLoading: boolean;
    campaignSearch: string;
    onCampaignSearch: (value: string) => void;
}

export const VerificationFilters = ({
    state,
    onChange,
    publisherOptions,
    campaignOptions,
    isPublishersLoading,
    isCampaignsLoading,
    campaignSearch,
    onCampaignSearch,
}: IVerificationFiltersProps) => {
    const { translate } = useTranslate();
    const loadingEmptyState = translate("ad.intelligence.verification.filters.loading");

    const adPositionOptions = useMemo<SelectOption[]>(
        () => [
            { id: "Above", value: "Above", label: translate("ad.intelligence.verification.filters.ad_position.above") },
            { id: "Below", value: "Below", label: translate("ad.intelligence.verification.filters.ad_position.below") },
            {
                id: "FarBelow",
                value: "FarBelow",
                label: translate("ad.intelligence.verification.filters.ad_position.far_below"),
            },
            { id: "Na", value: "Na", label: translate("ad.intelligence.verification.filters.ad_position.unknown") },
        ],
        [translate],
    );

    const publishersLabel = useMemo(
        () =>
            createMultiSelectLabel(
                translate("ad.intelligence.verification.filters.publishers.label"),
                (id) => publisherOptions.find((o) => o.value === id),
                (o) => o.label,
            ),
        [translate, publisherOptions],
    );
    const publishersTooltip = useMemo(() => createSelectedItemsTooltip(publisherOptions), [publisherOptions]);

    const campaignsLabel = useMemo(
        () =>
            createMultiSelectLabel(
                translate("ad.intelligence.verification.filters.campaigns.label"),
                (id) => campaignOptions.find((o) => o.value === id),
                (o) => o.label,
            ),
        [translate, campaignOptions],
    );
    const campaignsTooltip = useMemo(() => createSelectedItemsTooltip(campaignOptions), [campaignOptions]);

    const adPositionsLabel = useMemo(
        () =>
            createMultiSelectLabel(
                translate("ad.intelligence.verification.filters.ad_position.label"),
                (id) => adPositionOptions.find((o) => o.value === id),
                (o) => o.label,
            ),
        [translate, adPositionOptions],
    );
    const adPositionsTooltip = useMemo(() => createSelectedItemsTooltip(adPositionOptions), [adPositionOptions]);

    return (
        <Filters.Provider
            state={state}
            onChange={onChange}
        >
            <Flex gap="12px">
                <Filters.MultiSelect
                    name="publishers"
                    label={publishersLabel}
                    tooltip={publishersTooltip}
                    options={isPublishersLoading ? [] : publisherOptions}
                    emptyState={isPublishersLoading ? loadingEmptyState : undefined}
                    withSearch
                />
                <Filters.MultiSelect
                    name="campaigns"
                    label={campaignsLabel}
                    tooltip={campaignsTooltip}
                    options={campaignOptions}
                    isLoading={isCampaignsLoading}
                    searchValue={campaignSearch}
                    onSearch={onCampaignSearch}
                    renderItem={renderCampaignOption}
                    popoverContentProps={CAMPAIGNS_POPOVER_PROPS}
                    withSearch
                />
                <Filters.MultiSelect
                    name="adPositions"
                    label={adPositionsLabel}
                    tooltip={adPositionsTooltip}
                    options={adPositionOptions}
                    withSearch
                />
            </Flex>
        </Filters.Provider>
    );
};
